import { useEffect } from "react";
import { SITE_URL } from "@/lib/siteConfig";

/**
 * Head block for non-article marketing routes.
 *
 * PillarSeo covers editorial pages (it always emits Article schema). This one
 * covers product / overview routes: title, description, canonical, the full
 * Open Graph and Twitter set, and any JSON-LD the route wants to declare.
 *
 * Everything it writes is restored or removed on unmount, so one route's
 * preview never leaks into the next.
 */

export interface RouteSeoProps {
  /** Path after the origin, e.g. "/product". */
  path: string;
  title: string;
  description: string;
  /** Absolute URL or a path under public/. Defaults to the sitewide preview. */
  image?: string;
  imageAlt?: string;
  ogType?: string;
  noindex?: boolean;
  /** Extra JSON-LD blocks, each keyed by a stable id. */
  jsonLd?: { id: string; data: unknown }[];
  origin?: string;
}

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image-large.png`;
const DEFAULT_OG_IMAGE_ALT =
  "Myelina Health — AI energy prediction for people living with MS";

function upsertMeta(key: string, content: string, attr: "name" | "property" = "name") {
  const selector = `meta[${attr}="${key}"]`;
  const existing = document.querySelector(selector) as HTMLMetaElement | null;
  const el = existing ?? document.createElement("meta");
  const previous = existing?.getAttribute("content") ?? null;
  if (!existing) {
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (!existing) el.remove();
    else if (previous === null) el.removeAttribute("content");
    else el.setAttribute("content", previous);
  };
}

function upsertCanonical(href: string) {
  const existing = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  const el = existing ?? document.createElement("link");
  const previous = existing?.getAttribute("href") ?? null;
  if (!existing) {
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (!existing) el.remove();
    else if (previous === null) el.removeAttribute("href");
    else el.setAttribute("href", previous);
  };
}

function upsertJsonLd(id: string, data: unknown) {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  const el = existing ?? document.createElement("script");
  if (!existing) {
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => {
    el.remove();
  };
}

export const RouteSeo = ({
  path,
  title,
  description,
  image,
  imageAlt,
  ogType = "website",
  noindex = false,
  jsonLd = [],
  origin = SITE_URL,
}: RouteSeoProps) => {
  const serializedJsonLd = JSON.stringify(jsonLd);

  useEffect(() => {
    const base = origin.replace(/\/+$/, "");
    const canonical = `${base}${path.startsWith("/") ? path : `/${path}`}`;
    const resolvedImage = image
      ? image.startsWith("http")
        ? image
        : `${base}${image.startsWith("/") ? "" : "/"}${image}`
      : DEFAULT_OG_IMAGE;
    const resolvedAlt = imageAlt || DEFAULT_OG_IMAGE_ALT;

    const previousTitle = document.title;
    document.title = title;

    const restore = [
      upsertMeta("description", description),
      upsertMeta("robots", noindex ? "noindex, follow" : "index, follow"),
      upsertMeta("og:site_name", "Myelina Health", "property"),
      upsertMeta("og:locale", "en_US", "property"),
      upsertMeta("og:title", title, "property"),
      upsertMeta("og:description", description, "property"),
      upsertMeta("og:url", canonical, "property"),
      upsertMeta("og:type", ogType, "property"),
      upsertMeta("og:image", resolvedImage, "property"),
      upsertMeta("og:image:alt", resolvedAlt, "property"),
      upsertMeta("twitter:card", "summary_large_image"),
      upsertMeta("twitter:title", title),
      upsertMeta("twitter:description", description),
      upsertMeta("twitter:image", resolvedImage),
      upsertMeta("twitter:image:alt", resolvedAlt),
      upsertCanonical(canonical),
      ...(JSON.parse(serializedJsonLd) as { id: string; data: unknown }[]).map((b) =>
        upsertJsonLd(b.id, b.data),
      ),
    ];

    return () => {
      document.title = previousTitle;
      restore.reverse().forEach((undo) => undo());
    };
  }, [path, title, description, image, imageAlt, ogType, noindex, origin, serializedJsonLd]);

  return null;
};

export default RouteSeo;
